import { useCallback, useState } from 'react';
import { HttpMethod, HttpWrapper } from '../../../../../helpers/httpWrapper';
import {
	IPlatformAdminMobileAppGetRequest,
	IPlatformAdminMobileAppMarkPaidRequest,
	IPlatformAdminMobileAppMarkPaidResponse,
	IPlatformAdminMobileAppResponse,
	IPlatformAdminMobileAppStatusRequest,
	IPlatformAdminMobileAppStatusResponse,
	IPlatformAdminMobileAppStoreStatusRequest,
	IPlatformAdminMobileAppStoreStatusResponse,
	VPlatformAdminMobileAppGet,
	VPlatformAdminMobileAppMarkPaid,
	VPlatformAdminMobileAppStatus,
	VPlatformAdminMobileAppStoreStatus,
} from '../../../../../networked-shared-interfaces/platform-admin/mobile-app-route';
import {
	INetworkedPlusPlan,
	INetworkedPlusPlansResponse,
	IPlatformNetworkedPlusPlansRequest,
	VPlatformNetworkedPlusPlansGet,
} from '../../../../../networked-shared-interfaces/stripe-routes/networked-plus-plans';
import { URLWrapper } from '../../../../../networked-shared-interfaces/url-wrapper';
import { toastService } from '../../../../../services/toast.service';

interface IMobileAppState {
	loading: boolean;
	error: string | null;
	record: IPlatformAdminMobileAppResponse | null;
}

/**
 * The branded mobile app's staff controls.
 *
 * Same shape as `useProvisionOneSignalApp` — local `{ loading, error, result }`, direct
 * `HttpWrapper.callUrl` with the shared validator, `toastService` on both paths. This repo has
 * no query client, so state is per-hook and each write refetches rather than invalidating.
 *
 * Every write refetches before resolving, because these actions change each other's validity:
 * marking paid is what unblocks "In Development", and a stale record would leave the panel
 * offering a transition the server will now reject.
 */
export const useMobileAppStatus = (communityId: string) => {
	const [state, setState] = useState<IMobileAppState>({
		loading: false,
		error: null,
		record: null,
	});

	const fetchRecord = useCallback(async (): Promise<IPlatformAdminMobileAppResponse | null> => {
		setState(s => ({ ...s, loading: true, error: null }));
		try {
			const params: IPlatformAdminMobileAppGetRequest = { communityId };
			const res = await HttpWrapper.callUrl<IPlatformAdminMobileAppGetRequest, IPlatformAdminMobileAppResponse>(
				URLWrapper.urls.platformAdmin.mobileApp.url,
				HttpMethod.GET,
				params,
				VPlatformAdminMobileAppGet
			);
			setState({ loading: false, error: null, record: res });
			return res;
		} catch (err: any) {
			const message: string = err?.message || 'Failed to load the mobile app record.';
			setState(s => ({ ...s, loading: false, error: message }));
			return null;
		}
	}, [communityId]);

	const setStatus = useCallback(async (status: number, note?: string): Promise<boolean> => {
		setState(s => ({ ...s, loading: true, error: null }));
		try {
			const body: IPlatformAdminMobileAppStatusRequest = { communityId, status, note };
			const res = await HttpWrapper.callUrl<IPlatformAdminMobileAppStatusRequest, IPlatformAdminMobileAppStatusResponse>(
				URLWrapper.urls.platformAdmin.mobileApp.status,
				HttpMethod.PUT,
				body,
				VPlatformAdminMobileAppStatus
			);
			// Called out separately because it changes the community beyond this record — it is
			// what switches invite copy to the white-label wording.
			toastService.success(
				res?.markedNetworkedPlus
					? 'Status updated. This community is now marked Networked Plus.'
					: 'Status updated.'
			);
			await fetchRecord();
			return true;
		} catch (err: any) {
			const message: string = err?.message || 'Failed to update the status.';
			setState(s => ({ ...s, loading: false, error: message }));
			toastService.error(message);
			return false;
		}
	}, [communityId, fetchRecord]);

	const setStoreStatus = useCallback(async (
		store: number,
		versionLabel: string,
		status: number,
		notes?: string
	): Promise<boolean> => {
		setState(s => ({ ...s, loading: true, error: null }));
		try {
			const body: IPlatformAdminMobileAppStoreStatusRequest = {
				communityId,
				store,
				versionLabel,
				status,
				notes,
			};
			await HttpWrapper.callUrl<IPlatformAdminMobileAppStoreStatusRequest, IPlatformAdminMobileAppStoreStatusResponse>(
				URLWrapper.urls.platformAdmin.mobileApp.storeStatus,
				HttpMethod.PUT,
				body,
				VPlatformAdminMobileAppStoreStatus
			);
			toastService.success('Store status updated.');
			await fetchRecord();
			return true;
		} catch (err: any) {
			const message: string = err?.message || 'Failed to update the store status.';
			setState(s => ({ ...s, loading: false, error: message }));
			toastService.error(message);
			return false;
		}
	}, [communityId, fetchRecord]);

	/**
	 * The Plus tiers, for the mark-paid plan picker. Read from Stripe through the platform-admin
	 * route, so the planId sent is always one the token sync can resolve. An empty list on failure:
	 * the dialog then says so instead of offering nothing to pick.
	 */
	const fetchPlusPlans = useCallback(async (): Promise<INetworkedPlusPlan[]> => {
		try {
			const res = await HttpWrapper.callUrl<IPlatformNetworkedPlusPlansRequest, INetworkedPlusPlansResponse>(
				URLWrapper.urls.stripe.platformNetworkedPlusPlans,
				HttpMethod.GET,
				{},
				VPlatformNetworkedPlusPlansGet
			);
			return res?.plans ?? [];
		} catch (err: any) {
			toastService.error(err?.message || 'Could not load the Plus plans.');
			return [];
		}
	}, []);

	/**
	 * Marks the record paid offline for the given Plus tier. `planId` is required: an offline-paid
	 * community's token grant is resolved from it (Spencer's review Q15).
	 */
	const markPaid = useCallback(async (planId: string, note?: string): Promise<boolean> => {
		setState(s => ({ ...s, loading: true, error: null }));
		try {
			const body: IPlatformAdminMobileAppMarkPaidRequest = { communityId, planId, note };
			await HttpWrapper.callUrl<IPlatformAdminMobileAppMarkPaidRequest, IPlatformAdminMobileAppMarkPaidResponse>(
				URLWrapper.urls.platformAdmin.mobileApp.markPaid,
				HttpMethod.PUT,
				body,
				VPlatformAdminMobileAppMarkPaid
			);
			toastService.success('Marked as paid. Development is now unblocked.');
			await fetchRecord();
			return true;
		} catch (err: any) {
			const message: string = err?.message || 'Failed to mark as paid.';
			setState(s => ({ ...s, loading: false, error: message }));
			toastService.error(message);
			return false;
		}
	}, [communityId, fetchRecord]);

	const clearError = () => setState(s => ({ ...s, error: null }));

	return { ...state, fetchRecord, setStatus, setStoreStatus, markPaid, fetchPlusPlans, clearError };
};
